import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import ApplyForm from "@/pages/apply/components/ApplyForm";
import SuccessPanel from "@/pages/apply/components/SuccessPanel";
import MyApplications from "@/pages/apply/components/MyApplications";
import { useApplyForm } from "@/pages/apply/hooks/useApplyForm";

const STEP_KEYS = [
  { title: "apply.step1Title", desc: "apply.step1Desc" },
  { title: "apply.step2Title", desc: "apply.step2Desc" },
  { title: "apply.step3Title", desc: "apply.step3Desc" },
  { title: "apply.step4Title", desc: "apply.step4Desc" },
];

const TIP_KEYS = ["apply.tip1", "apply.tip2", "apply.tip3", "apply.tip4"];

export default function ApplyPage() {
  const { t } = useTranslation();
  const {
    products,
    productsLoading,
    productsError,
    reloadProducts,
    values,
    errors,
    updateField,
    submitting,
    submitError,
    submitted,
    submit,
    reset,
    selectedProduct,
    archivedProduct,
    recommendedProducts,
  } = useApplyForm();

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("apply.breadcrumbHome")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">{t("apply.breadcrumbCurrent")}</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
              {t("apply.title")}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {t("apply.subtitle")}
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {submitted ? (
                <SuccessPanel data={submitted} onReset={reset} />
              ) : (
                <ApplyForm
                  products={products}
                  productsLoading={productsLoading}
                  productsError={productsError}
                  onReloadProducts={reloadProducts}
                  values={values}
                  errors={errors}
                  onChange={updateField}
                  submitting={submitting}
                  submitError={submitError}
                  onSubmit={() => {
                    void submit();
                  }}
                  selectedProduct={selectedProduct}
                  archivedProduct={archivedProduct}
                  recommendedProducts={recommendedProducts}
                />
              )}
            </div>

            <aside className="flex flex-col gap-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("apply.flowTitle")}</h2>
                <ol className="mt-4 space-y-4">
                  {STEP_KEYS.map((step, index) => (
                    <li key={step.title} className="flex gap-3">
                      <span className="flex flex-col items-center">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
                          {index + 1}
                        </span>
                        {index < STEP_KEYS.length - 1 && <span className="mt-1 w-px flex-1 bg-background-200" />}
                      </span>
                      <div className="pb-1">
                        <p className="text-sm font-medium text-foreground-950">{t(step.title)}</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-foreground-600">{t(step.desc)}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("apply.tipsTitle")}</h2>
                <ul className="mt-3 space-y-2.5">
                  {TIP_KEYS.map((tipKey) => (
                    <li key={tipKey} className="flex items-start gap-2 text-xs leading-relaxed text-foreground-700">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                        <i className="ri-checkbox-circle-line" />
                      </span>
                      {t(tipKey)}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("apply.helpTitle")}</h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{t("apply.helpDesc")}</p>
                <Link
                  to="/products"
                  className="mt-3 inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-primary-700 transition-colors hover:text-primary-600"
                >
                  {t("apply.browseProducts")}
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </span>
                </Link>
              </div>
            </aside>
          </div>
        </section>

        <MyApplications />
      </main>

      <AppFooter />
    </div>
  );
}